import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import cn from 'clsx';
import useSwr, { mutate } from 'swr';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import Toggle from '@components/elements/Toggle';
import Card from '@components/elements/Card';
import { Colors } from '@components/elements/Colors';
import Dialog from '@components/elements/Dialog';
import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';

import { useAuth } from '@hooks/Auth';
import {
  listDomainByCompany,
  getCompanyByUserID,
  createCompanyCustomTheme,
  getCompanyThemesByCompanyID,
  updateCompanyCustomTheme,
  updateCompany,
  companyRegisterDomain,
  removeDomain,
} from '@sdk/managers/UsersManager';
import { useGlobalContext } from '@context/customContext';

const DOMAIN_REGEX =
  /[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@%_\\+.~#?&//=]*)/;

const FORM_SCHEMA = z.object({
  name: z.string().min(2).max(99),
  domain: z.string().regex(DOMAIN_REGEX, {
    message: 'Invalid domain format e.g. megax.world',
  }),
  autoDomain: z.boolean(),
});

const THEME_SCHEMA = z.object({
  pollActivePeriodBgColor: z.string(),
  pollActivePeriodShadowColor: z.string(),

  pollQuestionCardBgColor: z.string(),
  pollQuestionCardQuestionColor: z.string(),
  pollQuestionCardAnswerColor: z.string(),

  pollPledgeCastBtnBgColor: z.string(),
  pollPledgeCastBtnBracketColor: z.string(),
  pollPledgeCastBtnTextColor: z.string(),

  pollPageBgColor: z.string(),
});

type FormInput = z.infer<typeof FORM_SCHEMA>;
type ThemeData = z.infer<typeof THEME_SCHEMA>;

const CompanySettingsPage: FC = () => {
  const { dbUser, company: userCompany } = useAuth();
  const [modal, setModal] = useState<boolean>(false);
  const [showCustomForm, setShowCustomForm] = useState<boolean>(false);
  const [isEditingTheme, setIsEditingTheme] = useState<boolean>(false);
  const formRef = useRef<HTMLDivElement>(null);
  const { customTheme, setCustomTheme } = useGlobalContext();

  const hideModal = useCallback(() => setModal(false), [setModal]);
  // const showModal = useCallback(() => setModal(true), [setModal]);

  const { data: company, error } = useSwr(
    dbUser?.id ? ['/user/company', dbUser.id] : null,
    async (_query, userId) => {
      let domains;
      if (userCompany?.companyID) {
        domains = (await listDomainByCompany(userCompany.companyID)) as {
          data?: {
            result?: {
              items: {
                id: string;
                domain: string;
              }[];
            };
          };
        };
      }
      return {
        ...userCompany,
        domains: domains?.data?.result?.items || [],
      };
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch company', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  console.log(company, company?.domains?.[0]?.domain);

  const form = useForm<FormInput>({
    reValidateMode: 'onChange',
    resolver: zodResolver(FORM_SCHEMA),
  });

  const themeForm = useForm<ThemeData>({
    reValidateMode: 'onChange',
    resolver: zodResolver(THEME_SCHEMA),
  });

  useEffect(() => {
    if (formRef && formRef.current) {
      formRef.current.scrollIntoView({ block: 'start' });
    }
    const timer = setTimeout(() => {
      setShowCustomForm(true);
    }, 100);

    if (!company) return;
    form.reset({
      name: company.name,
      domain: company.domain,
      autoDomain:
        company.domains?.findIndex((d) => d.domain === company.domain) !== -1,
    });

    return () => {
      clearTimeout(timer);
    };
  }, [company, form, themeForm]);

  useEffect(() => {
    if (!company || !dbUser) return;
    if (customTheme && Object.keys(customTheme).length > 0) {
      const themeAttributes = customTheme?.attributes;
      if (themeAttributes) {
        themeForm.reset({
          pollActivePeriodBgColor: themeAttributes.pollActivePeriodBgColor,
          pollActivePeriodShadowColor:
            themeAttributes.pollActivePeriodShadowColor,
          pollQuestionCardBgColor: themeAttributes.pollQuestionCardBgColor,
          pollQuestionCardQuestionColor:
            themeAttributes.pollQuestionCardQuestionColor,
          pollQuestionCardAnswerColor:
            themeAttributes.pollQuestionCardAnswerColor,
          pollPledgeCastBtnBgColor: themeAttributes.pollPledgeCastBtnBgColor,
          pollPledgeCastBtnBracketColor:
            themeAttributes.pollPledgeCastBtnBracketColor,
          pollPledgeCastBtnTextColor:
            themeAttributes.pollPledgeCastBtnTextColor,
          pollPageBgColor: themeAttributes.pollPageBgColor,
        });
        setIsEditingTheme(true);
      }
    } else {
      setIsEditingTheme(false);
    }
  }, [company, dbUser, customTheme, themeForm]);

  const loading = !error && !company;

  const onSubmit = useCallback(
    async (data: FormInput) => {
      try {
        if (!dbUser || !company?.companyID)
          return toast.error('Unexpected error.');
        console.log('onSubmit', data);

        const action = async () => {
          if (!company?.companyID) throw new Error('No company found.');
          console.log('company.companyID', company.companyID);
          const companyID = company.companyID;
          let actions: Promise<any>[] = [];
          actions.push(
            updateCompany({
              id: companyID,
              name: data.name,
              domain: data.domain,
            }) as Promise<any>
          );
          if (data.autoDomain) {
            if (company?.domains) {
              await Promise.all(
                company.domains.map((domain) => removeDomain(domain.id))
              );
            }
            actions.push(
              companyRegisterDomain(companyID, data.domain) as Promise<any>
            );
          }
          await Promise.all(actions);
          console.log('domain updates', await Promise.all(actions));
        };

        await toast.promise(action(), {
          loading: `Updating company details...`,
          success: `Successfully updated company details`,
          error: (err) =>
            err.error || err.message || `Failed to update company details`,
        });

        await Promise.all([
          mutate(['/user/company', dbUser.id]),
          mutate(['/user/company/domain', dbUser.id]),
        ]);
      } catch (err: any) {
        console.log('Failed updateCompany', err);
      }
    },
    [company, dbUser]
  );

  const onSubmitTheme = useCallback(
    async (data: ThemeData) => {
      try {
        if (!dbUser) return toast.error('Unexpected error.');

        const dbPayload = {
          id: customTheme?.id,
          companyID: company?.companyID || '',
          attributes: data,
        };
        if (isEditingTheme) {
          const res = await updateCompanyCustomTheme(dbPayload);
          if (res) {
            const themeResult = res?.data?.result;
            themeResult && setCustomTheme(themeResult);
            toast.success('Successfully updated custom theme');
          }
        } else {
          const res = await createCompanyCustomTheme(dbPayload);
          if (res) {
            const themeResult = res?.data?.result;
            themeResult && setCustomTheme(themeResult);
            toast.success('Successfully updated custom theme');
          }
        }
      } catch (err: any) {
        console.log('Failed assignRepresentative', err);
        toast.error('Unexpected error.');
      }
    },
    [dbUser, customTheme, company, isEditingTheme, setCustomTheme]
  );

  console.log(form.formState.errors);

  return (
    <React.Fragment>
      <Dialog
        className="text-center text-black bg-yellow"
        title="Add representative"
        content={
          <div>
            <p className="mb-4 text-sm">
              Enter representative&apos;s email here
            </p>
          </div>
        }
        open={modal}
        onCancel={hideModal}
        onConfirm={() => {
          hideModal();
        }}
      />
      <div
        ref={formRef}
        className="flex flex-col min-h-screen px-4 pt-16 pb-32 space-y-4 bg-blue-light"
      >
        <div>
          <h1 className="text-4xl text-white whitespace-pre-line">{`Company 
        Settings`}</h1>
          <p className="mt-4 text-sm text-white">
            Update your organisations settings
          </p>
        </div>
        {/* <div>
          <div
            role="button"
            className="flex p-4 text-black bg-yellow "
            onClick={showModal}
          >
            <PlusIcon className="mr-2" />
            Add representative
          </div>
        </div> */}
        <div className="flex">
          <Card shadowColor="yellow" className="w-full p-4 xl:max-w-lg">
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="name" className="block mb-1 font-semibold">
                  Company Name
                </label>
                <InputText
                  type="text"
                  placeholder="MegaX World"
                  borderColor={Colors.magenta}
                  shadowColor={Colors.yellow}
                  disabled={!error && !company}
                  {...form.register('name')}
                />
                {form.formState.errors.name && (
                  <label htmlFor="name" className="block mt-1 text-red">
                    {form.formState.errors.name.message}
                  </label>
                )}
              </div>
              <div>
                <label htmlFor="name" className="block mb-1 font-semibold">
                  Domain
                </label>
                <InputText
                  type="text"
                  placeholder="megax.world"
                  borderColor={Colors.magenta}
                  shadowColor={Colors.yellow}
                  disabled={!error && !company}
                  {...form.register('domain')}
                />
                {form.formState.errors.domain && (
                  <label htmlFor="domain" className="block mt-1 text-red">
                    {form.formState.errors.domain.message}
                  </label>
                )}
              </div>

              <div>
                <Controller
                  name="autoDomain"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex justify-between font-semibold">
                      <label htmlFor="autoDomain">
                        Domain auto member approval
                      </label>
                      <div className="flex items-center">
                        <Toggle {...field} activeClassName="bg-magenta" />
                      </div>
                    </div>
                  )}
                />
              </div>

              <div className="pt-1">
                <ButtonMighty
                  type="submit"
                  className={cn('w-full xl:w-auto', {
                    'opacity-70': !form.formState.isDirty,
                  })}
                  disabled={
                    !company ||
                    !form.formState.isDirty ||
                    form.formState.isSubmitting
                  }
                  loading={form.formState.isSubmitting || loading}
                  color="#FFFF00"
                  borderColor="#ff1dfc"
                  textColor="#5600fb"
                  text="Update company"
                />
              </div>
            </form>
          </Card>
        </div>
        {showCustomForm && (
          <div className="w-full pt-6">
            <h2 className="w-full mb-3 mr-4 text-2xl text-white">
              Edit Custom Enterprise Theme
            </h2>

            <form
              className="space-y-4"
              onSubmit={themeForm.handleSubmit(onSubmitTheme)}
            >
              <div className="pb-4">
                <Card shadowColor="yellow" className="p-4 space-y-4">
                  <h3>Vote Pledging Active Period</h3>
                  <div className="grid grid-cols-3 gap-5">
                    <div>
                      <label
                        htmlFor="pollActivePeriodBgColor"
                        className="block mb-1 font-semibold"
                      >
                        Background Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollActivePeriodBgColor')}
                      />
                      {themeForm.formState.errors.pollActivePeriodBgColor && (
                        <label
                          htmlFor="pollActivePeriodBgColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors.pollActivePeriodBgColor
                              .message
                          }
                        </label>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="pollActivePeriodShadowColor"
                        className="block mb-1 font-semibold"
                      >
                        Shadow Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollActivePeriodShadowColor')}
                      />
                      {themeForm.formState.errors
                        .pollActivePeriodShadowColor && (
                        <label
                          htmlFor="pollActivePeriodShadowColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors
                              .pollActivePeriodShadowColor.message
                          }
                        </label>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              <div className="pb-4">
                <Card shadowColor="yellow" className="p-4 space-y-4">
                  <h3 className="">Question Card</h3>
                  <div className="grid grid-cols-3 gap-5">
                    <div>
                      <label
                        htmlFor="pollQuestionCardBgColor"
                        className="block mb-1 font-semibold"
                      >
                        Background Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollQuestionCardBgColor')}
                      />
                      {themeForm.formState.errors.pollQuestionCardBgColor && (
                        <label
                          htmlFor="pollQuestionCardBgColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors.pollQuestionCardBgColor
                              .message
                          }
                        </label>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="pollQuestionCardQuestionColor"
                        className="block mb-1 font-semibold"
                      >
                        Question Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollQuestionCardQuestionColor')}
                      />
                      {themeForm.formState.errors
                        .pollQuestionCardQuestionColor && (
                        <label
                          htmlFor="pollQuestionCardQuestionColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors
                              .pollQuestionCardQuestionColor.message
                          }
                        </label>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="pollQuestionCardAnswerColor"
                        className="block mb-1 font-semibold"
                      >
                        Answer Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollQuestionCardAnswerColor')}
                      />
                      {themeForm.formState.errors
                        .pollQuestionCardAnswerColor && (
                        <label
                          htmlFor="pollQuestionCardAnswerColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors
                              .pollQuestionCardAnswerColor.message
                          }
                        </label>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              <div className="pb-4">
                <Card shadowColor="yellow" className="p-4 space-y-4">
                  <h3>Pledge and Vote Casting Button</h3>
                  <div className="grid grid-cols-3 gap-5">
                    <div>
                      <label
                        htmlFor="pollPledgeCastBtnBgColor"
                        className="block mb-1 font-semibold"
                      >
                        Background Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollPledgeCastBtnBgColor')}
                      />
                      {themeForm.formState.errors.pollPledgeCastBtnBgColor && (
                        <label
                          htmlFor="pollPledgeCastBtnBgColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors.pollPledgeCastBtnBgColor
                              .message
                          }
                        </label>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="pollPledgeCastBtnBracketColor"
                        className="block mb-1 font-semibold"
                      >
                        Bracket Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollPledgeCastBtnBracketColor')}
                      />
                      {themeForm.formState.errors
                        .pollPledgeCastBtnBracketColor && (
                        <label
                          htmlFor="pollPledgeCastBtnBracketColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors
                              .pollPledgeCastBtnBracketColor.message
                          }
                        </label>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="pollPledgeCastBtnTextColor"
                        className="block mb-1 font-semibold"
                      >
                        Text Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollPledgeCastBtnTextColor')}
                      />
                      {themeForm.formState.errors
                        .pollPledgeCastBtnTextColor && (
                        <label
                          htmlFor="pollPledgeCastBtnTextColor"
                          className="block mt-1 text-red"
                        >
                          {
                            themeForm.formState.errors
                              .pollPledgeCastBtnTextColor.message
                          }
                        </label>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              <div className="pb-4">
                <Card shadowColor="yellow" className="p-4 space-y-4">
                  <h3>Poll Page Background Color</h3>
                  <div className="grid grid-cols-3 gap-5">
                    <div>
                      <label
                        htmlFor="pollPageBgColor"
                        className="block mb-1 font-semibold"
                      >
                        Background Color
                      </label>
                      <InputText
                        type="text"
                        placeholder="MegaX World"
                        borderColor={Colors.magenta}
                        shadowColor={Colors.yellow}
                        disabled={!error && !company}
                        {...themeForm.register('pollPageBgColor')}
                      />
                      {themeForm.formState.errors.pollPageBgColor && (
                        <label
                          htmlFor="pollPageBgColor"
                          className="block mt-1 text-red"
                        >
                          {themeForm.formState.errors.pollPageBgColor.message}
                        </label>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              <div className="pt-1">
                <ButtonMighty
                  type="submit"
                  className={cn('w-96', {
                    'opacity-70': !themeForm.formState.isDirty,
                  })}
                  disabled={
                    !themeForm.formState.isDirty ||
                    themeForm.formState.isSubmitting
                  }
                  loading={themeForm.formState.isSubmitting || loading}
                  color="#FFFF00"
                  borderColor="#ff1dfc"
                  textColor="#5600fb"
                  text="Update Theme"
                />
              </div>
            </form>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default CompanySettingsPage;
